import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSampleData } from './SampleDataContext';
import { Space, Alert, Typography, Radio, Tooltip, Button } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CodeOutlined,
    FormOutlined,
    EyeOutlined,
    EyeInvisibleOutlined
} from '@ant-design/icons';
import SampleDataViewer from './SampleDataViewer';
import Editor from '@monaco-editor/react';

const { Text } = Typography;

const JavaScriptExpressionEditor = ({
                                        value,
                                        onChange,
                                        multiline = false,
                                        height = multiline ? 400 : 36,
                                        placeholder,
                                        evaluateOnChange = false,
                                        label = null,
                                        ultraCompact = false, // Added ultraCompact mode prop
                                    }) => {
    const { currentSample } = useSampleData();
    const [expression, setExpression] = useState(value || '');
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [evaluationError, setEvaluationError] = useState(null);
    const [mode, setMode] = useState('expression'); // 'expression' or 'function'
    const [shouldShowUndefined, setShouldShowUndefined] = useState(false);
    const [showSampleData, setShowSampleData] = useState(false);

    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const sampleDataRef = useRef(currentSample);

    // Custom styles for ultra-compact mode
    const ultraCompactStyles = ultraCompact ? {
        editorContainer: {
            position: 'relative',
        },
        indicatorContainer: {
            position: 'absolute',
            right: 8,
            top: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            pointerEvents: 'none', // Let clicks pass through to editor
            zIndex: 5,
        },
        indicator: {
            fontSize: 14,
            pointerEvents: 'auto', // Make icon clickable
            opacity: 0.8,
            padding: 2,
            cursor: 'pointer',
        },
        editor: {
            paddingRight: 60, // Make room for indicators
        }
    } : {};

    // Changed default placeholders to avoid using record.id
    const defaultPlaceholders = {
        expression: {
            single: "record",
            multi: "record"
        },
        function: {
            single: "(record, workflow) => record",
            multi: "(record, workflow) => {\n  // Return an updated record or value\n  return record;\n}"
        }
    };

    // If no placeholder is provided, use the default based on mode and multiline
    const effectivePlaceholder = placeholder ||
        (multiline ? defaultPlaceholders[mode].multi : defaultPlaceholders[mode].single);

    // Update sample data ref when it changes and re-evaluate expression
    useEffect(() => {
        sampleDataRef.current = currentSample;
        // Only re-evaluate if we have sample data and expression
        if (expression && currentSample) {
            try {
                evaluateExpression(expression);
            } catch (e) {
                console.error("Error evaluating with updated sample:", e);
            }
        }
    }, [currentSample]);

    // Get editor options including ultra-compact mode adjustments
    const getEditorOptions = () => {
        return {
            lineNumbers: ultraCompact || !multiline ? 'off' : 'on',
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            wordWrap: 'on',
            automaticLayout: true,
            quickSuggestions: { other: true, comments: true, strings: true },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            fontSize: 14,
            lineHeight: 22,
            padding: {
                top: 6,
                bottom: 6,
                right: ultraCompact ? 60 : 6,
                left: 6
            },
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            renderLineHighlight: 'none',
            suggest: {
                maxVisibleSuggestions: 15,
                showValues: false,
            },
            contextmenu: false,
            renderIndentGuides: multiline,
            folding: multiline,
            selectionHighlight: false,
            occurrencesHighlight: false,
        };
    };

    // Safe mode change handler
    const handleModeChange = (newMode) => {
        setMode(newMode);

        // Always update with new default when changing modes
        if (editorRef.current) {
            const newPlaceholder = multiline
                ? defaultPlaceholders[newMode].multi
                : defaultPlaceholders[newMode].single;

            editorRef.current.setValue(newPlaceholder);
            setExpression(newPlaceholder);
            if (onChange) onChange(newPlaceholder);

            // Only trigger evaluation if currentSample exists
            if (currentSample) {
                setTimeout(() => {
                    try {
                        evaluateExpression(newPlaceholder);
                    } catch (e) {
                        console.error("Error evaluating expression after mode change:", e);
                    }
                }, 100);
            }
        }
    };

    const insertTextAtCursor = useCallback(
        (text) => {
            if (!editorRef.current) return;
            const editor = editorRef.current;
            const selection = editor.getSelection();
            if (selection) {
                const range = {
                    startLineNumber: selection.startLineNumber,
                    startColumn: selection.startColumn,
                    endLineNumber: selection.endLineNumber,
                    endColumn: selection.endColumn,
                };
                editor.executeEdits('insert-text', [{ range, text, forceMoveMarkers: true }]);
                editor.focus();
                const newValue = editor.getValue();
                setExpression(newValue);
                if (onChange) onChange(newValue);
                if (evaluateOnChange && currentSample) {
                    evaluateExpression(newValue);
                }
            }
        },
        [onChange, evaluateOnChange, currentSample]
    );

    // Safer expression evaluation with improved handling of undefined/null results
    const evaluateExpression = useCallback(
        (expressionToEval = expression) => {
            // Always reset showUndefined flag when evaluating
            setShouldShowUndefined(false);

            // Early returns for invalid inputs
            if (!expressionToEval || typeof expressionToEval !== 'string' || !expressionToEval.trim()) {
                setEvaluationResult(null);
                setEvaluationError(null);
                return;
            }

            if (!currentSample) {
                setEvaluationResult(null);
                setEvaluationError("No sample data available");
                return;
            }

            try {
                let result;
                const safeWorkflow = { id: 'test-workflow' }; // Always provide a safe workflow object

                if (mode === 'function') {
                    const funcText = expressionToEval.trim();

                    try {
                        // Handle arrow functions
                        if (funcText.includes('=>')) {
                            // Use Function constructor instead of eval for better safety
                            const fnText = funcText.startsWith('(') ? funcText : `(${funcText})`;
                            const fnWrapper = new Function('return ' + fnText)();
                            result = fnWrapper(currentSample, safeWorkflow);
                        } else {
                            // For regular expressions/code
                            const func = new Function('record', 'workflow', `return ${funcText};`);
                            result = func(currentSample, safeWorkflow);
                        }
                    } catch (fnError) {
                        throw new Error(`Function error: ${fnError.message}`);
                    }
                } else {
                    // Expression mode - SIMPLIFIED to always add return unless already there
                    const hasReturn = expressionToEval.trim().startsWith('return ');
                    const functionBody = hasReturn
                        ? expressionToEval
                        : `return ${expressionToEval};`;

                    try {
                        const func = new Function('record', 'workflow', functionBody);
                        result = func(currentSample, safeWorkflow);
                    } catch (exprError) {
                        throw new Error(`Expression error: ${exprError.message}`);
                    }
                }

                // Explicitly set flag for undefined/null results to display them
                if (result === undefined || result === null) {
                    setShouldShowUndefined(true);
                }

                setEvaluationResult(result);
                setEvaluationError(null);
            } catch (error) {
                console.error("Evaluation error:", error);
                setEvaluationResult(null);
                setEvaluationError(error.message || "Unknown error");
            }
        },
        [expression, currentSample, multiline, mode]
    );

    const handleExpressionChange = (newValue) => {
        setExpression(newValue);
        if (onChange) onChange(newValue);

        // Always evaluate on change, regardless of evaluateOnChange setting
        if (currentSample) {
            if (window.evalTimeout) clearTimeout(window.evalTimeout);
            window.evalTimeout = setTimeout(() => {
                try {
                    evaluateExpression(newValue);
                } catch (e) {
                    console.error("Error during delayed evaluation:", e);
                }
            }, 300);
        }
    };

    const renderEvaluationResult = () => {
        // Always show error if present
        if (evaluationError) {
            return (
                <Alert
                    message={evaluationError}
                    type="error"
                    showIcon
                    icon={<CloseCircleOutlined />}
                    style={{ marginTop: 8 }}
                />
            );
        }

        // Show result even if null/undefined when the flag is true
        if (evaluationResult !== undefined || shouldShowUndefined) {
            const resultType = typeof evaluationResult;
            let displayValue;

            if (evaluationResult === null) {
                displayValue = "null";
            } else if (evaluationResult === undefined) {
                displayValue = "undefined";
            } else if (resultType === 'object') {
                displayValue = JSON.stringify(evaluationResult, null, 2);
            } else {
                displayValue = String(evaluationResult);
            }

            return (
                <Alert
                    message={
                        <Space>
                            <span>Result: <Text code>{displayValue}</Text></span>
                            <Text type="secondary">({evaluationResult === null ? 'null' : resultType})</Text>
                        </Space>
                    }
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginTop: 8 }}
                />
            );
        }

        return null;
    };

    // Helper function to format property values for display
    const formatPropertyDetail = (value) => {
        const valueType = typeof value;
        if (valueType === 'string') {
            return `string: "${value.length > 20 ? value.substring(0, 20) + '...' : value}"`;
        } else if (valueType === 'number') {
            return `number: ${value}`;
        } else if (valueType === 'boolean') {
            return `boolean: ${value}`;
        } else if (valueType === 'object' && value !== null) {
            if (Array.isArray(value)) {
                return `array[${value.length}]`;
            } else {
                const objKeys = Object.keys(value);
                return `object: {${objKeys.slice(0, 3).join(', ')}${objKeys.length > 3 ? ', ...' : ''}}`;
            }
        }
        return valueType;
    };

    // Improved editor mount handler with context-aware suggestions
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;

        if (!editor.getValue() && effectivePlaceholder) {
            editor.setValue(effectivePlaceholder);
            setExpression(effectivePlaceholder);
        }
        setTimeout(() => editor.focus(), 100);

        // Cleanup existing completion providers to avoid conflicts
        if (window.monacoCompletionDisposables) {
            window.monacoCompletionDisposables.forEach(d => d.dispose());
        }
        window.monacoCompletionDisposables = [];

        // Root objects provider (record, workflow) - with improved context handling
        const rootProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            triggerCharacters: [' ', '\t', '\n', '(', '{', '[', ',', ';'],
            provideCompletionItems: (model, position) => {
                const lineText = model.getLineContent(position.lineNumber);
                const wordUntilPosition = model.getWordUntilPosition(position);
                const textUntilPosition = lineText.substring(0, position.column - 1);

                // Don't suggest in property chains (.record is invalid)
                if (textUntilPosition.endsWith('.')) {
                    return { suggestions: [] };
                }

                // Don't suggest 'record' if we're already inside a record reference
                if (/\brecord\b/.test(textUntilPosition)) {
                    return { suggestions: [] };
                }

                // Don't suggest 'workflow' if we're already inside a workflow reference
                if (/\bworkflow\b/.test(textUntilPosition)) {
                    return { suggestions: [] };
                }

                // Additional check for comments or strings to avoid unwanted suggestions
                const isInComment = /\/\/.*$/.test(textUntilPosition);
                const isInString = (textUntilPosition.match(/"/g) || []).length % 2 !== 0 ||
                    (textUntilPosition.match(/'/g) || []).length % 2 !== 0;

                if (isInComment || isInString) {
                    return { suggestions: [] };
                }

                const currentWord = wordUntilPosition.word;
                const suggestions = [];

                // Only suggest root objects in valid contexts
                if (!currentWord || 'record'.startsWith(currentWord)) {
                    suggestions.push({
                        label: 'record',
                        kind: monaco.languages.CompletionItemKind.Variable,
                        detail: 'Current sample record',
                        insertText: 'record',
                        sortText: '00',
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: wordUntilPosition.startColumn,
                            endLineNumber: position.lineNumber,
                            endColumn: wordUntilPosition.endColumn
                        }
                    });
                }

                if (!currentWord || 'workflow'.startsWith(currentWord)) {
                    suggestions.push({
                        label: 'workflow',
                        kind: monaco.languages.CompletionItemKind.Variable,
                        detail: 'Workflow context',
                        insertText: 'workflow',
                        sortText: '01',
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: wordUntilPosition.startColumn,
                            endLineNumber: position.lineNumber,
                            endColumn: wordUntilPosition.endColumn
                        }
                    });
                }

                return { suggestions };
            }
        });
        window.monacoCompletionDisposables.push(rootProvider);

        // Dedicated property provider for "record."
        const recordProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            triggerCharacters: ['.'],
            provideCompletionItems: (model, position) => {
                const lineText = model.getLineContent(position.lineNumber);
                const textBeforeCursor = lineText.substring(0, position.column - 1);

                // Match exactly "record."
                if (textBeforeCursor.endsWith('record.')) {
                    if (!sampleDataRef.current) return { suggestions: [] };

                    // Get word information for proper range calculation
                    const wordUntilPosition = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    // Get all keys from the sample
                    const suggestions = Object.keys(sampleDataRef.current).map((key, index) => {
                        const value = sampleDataRef.current[key];
                        return {
                            label: key,
                            kind: monaco.languages.CompletionItemKind.Property,
                            detail: formatPropertyDetail(value),
                            insertText: key,
                            sortText: String(index).padStart(5, '0'),
                            range: range
                        };
                    });

                    return { suggestions };
                }

                return { suggestions: [] };
            }
        });
        window.monacoCompletionDisposables.push(recordProvider);

        // Dedicated provider for "workflow."
        const workflowProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            triggerCharacters: ['.'],
            provideCompletionItems: (model, position) => {
                const lineText = model.getLineContent(position.lineNumber);
                const textBeforeCursor = lineText.substring(0, position.column - 1);

                // Only trigger for exact "workflow."
                if (textBeforeCursor.endsWith('workflow.')) {
                    const wordUntilPosition = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    const suggestions = [
                        {
                            label: 'id',
                            kind: monaco.languages.CompletionItemKind.Property,
                            detail: 'Workflow identifier',
                            insertText: 'id',
                            range: range
                        }
                    ];

                    return { suggestions };
                }

                return { suggestions: [] };
            }
        });
        window.monacoCompletionDisposables.push(workflowProvider);

        // Dedicated provider for nested properties using regex
        const nestedProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            triggerCharacters: ['.'],
            provideCompletionItems: (model, position) => {
                if (!sampleDataRef.current) return { suggestions: [] };

                const lineText = model.getLineContent(position.lineNumber);
                const textBeforeCursor = lineText.substring(0, position.column - 1);

                // Match for patterns like "record.field."
                const nestedMatch = /\brecord\.([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\.$/i.exec(textBeforeCursor);
                if (nestedMatch) {
                    const path = nestedMatch[1].split('.');
                    let obj = sampleDataRef.current;

                    // Navigate to the correct nested object
                    for (const part of path) {
                        if (!obj || typeof obj !== 'object' || !(part in obj)) {
                            return { suggestions: [] };
                        }
                        obj = obj[part];
                    }

                    // Only continue if we found a valid object
                    if (!obj || typeof obj !== 'object') {
                        return { suggestions: [] };
                    }

                    // Get word information for proper range calculation
                    const wordUntilPosition = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    // Create suggestions for all keys in the nested object
                    const suggestions = Object.keys(obj).map((key, index) => {
                        const value = obj[key];
                        return {
                            label: key,
                            kind: monaco.languages.CompletionItemKind.Property,
                            detail: formatPropertyDetail(value),
                            insertText: key,
                            sortText: String(index).padStart(5, '0'),
                            range: range
                        };
                    });

                    return { suggestions };
                }

                return { suggestions: [] };
            }
        });
        window.monacoCompletionDisposables.push(nestedProvider);

        // Provider for partial property matches (record.abc...)
        const partialPropertyProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            provideCompletionItems: (model, position) => {
                if (!sampleDataRef.current) return { suggestions: [] };

                const lineText = model.getLineContent(position.lineNumber);
                const textUntilPosition = lineText.substring(0, position.column);

                // Match for partial property names after record.
                const partialMatch = /\brecord\.([a-zA-Z0-9_]*)$/i.exec(textUntilPosition);
                if (partialMatch) {
                    const prefix = partialMatch[1];

                    // Get word information for proper range calculation
                    const wordUntilPosition = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: position.column - prefix.length,
                        endLineNumber: position.lineNumber,
                        endColumn: position.column
                    };

                    // Filter properties by prefix
                    const filteredProperties = Object.keys(sampleDataRef.current)
                        .filter(key => key.toLowerCase().startsWith(prefix.toLowerCase()));

                    // Create suggestions
                    const suggestions = filteredProperties.map((key, index) => {
                        const value = sampleDataRef.current[key];
                        return {
                            label: key,
                            kind: monaco.languages.CompletionItemKind.Property,
                            detail: formatPropertyDetail(value),
                            insertText: key,
                            sortText: String(index).padStart(5, '0'),
                            range: range,
                            filterText: key
                        };
                    });

                    return { suggestions };
                }

                return { suggestions: [] };
            }
        });
        window.monacoCompletionDisposables.push(partialPropertyProvider);

        // Initial evaluation if we have sample data
        if (sampleDataRef.current) {
            try {
                evaluateExpression(editor.getValue());
            } catch (e) {
                console.error("Error during initial evaluation:", e);
            }
        }
    };

    useEffect(() => {
        if (value !== undefined && value !== expression) {
            setExpression(value);
        }
    }, [value]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Clean up completion providers
            if (window.monacoCompletionDisposables) {
                window.monacoCompletionDisposables.forEach(d => {
                    try { d.dispose(); } catch(e) {}
                });
                window.monacoCompletionDisposables = [];
            }
        };
    }, []);

    // Toggle sample data visibility
    const toggleSampleData = () => {
        setShowSampleData(!showSampleData);
    };

    // Render ultra-compact indicators inside the editor
    const renderUltraCompactIndicators = () => {
        if (!ultraCompact) return null;

        const showStatusIcon = evaluationResult !== null || evaluationError !== null || shouldShowUndefined;

        return (
            <div style={ultraCompactStyles.indicatorContainer}>
                {showStatusIcon && (
                    <Tooltip title={evaluationError || `Sample Output: ${evaluationResult === undefined ? 'undefined' :
                        evaluationResult === null ? 'null' :
                            typeof evaluationResult === 'object' ? JSON.stringify(evaluationResult) :
                                String(evaluationResult)}`}>
                        {evaluationError ?
                            <CloseCircleOutlined style={{ ...ultraCompactStyles.indicator, color: '#ff4d4f' }} /> :
                            <CheckCircleOutlined style={{ ...ultraCompactStyles.indicator, color: '#52c41a' }} />
                        }
                    </Tooltip>
                )}
                <Tooltip title={showSampleData ? "Hide sample data" : "Show sample data"}>
                    {showSampleData ?
                        <EyeInvisibleOutlined
                            style={{ ...ultraCompactStyles.indicator, color: '#1890ff' }}
                            onClick={toggleSampleData}
                        /> :
                        <EyeOutlined
                            style={{ ...ultraCompactStyles.indicator, color: '#1890ff' }}
                            onClick={toggleSampleData}
                        />
                    }
                </Tooltip>
            </div>
        );
    };

    // Render compact evaluation result for non-ultraCompact mode
    const renderCompactEvaluationResult = () => {
        if (evaluationError) {
            return (
                <Space size="small">
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    <Text type="danger" style={{ fontSize: '12px' }}>
                        {evaluationError.length > 60 ? evaluationError.substring(0, 60) + '...' : evaluationError}
                    </Text>
                </Space>
            );
        }

        if (evaluationResult !== undefined || shouldShowUndefined) {
            let displayValue;
            if (evaluationResult === undefined) {
                displayValue = "undefined";
            } else if (evaluationResult === null) {
                displayValue = "null";
            } else if (typeof evaluationResult === 'object') {
                displayValue = JSON.stringify(evaluationResult);
            } else {
                displayValue = String(evaluationResult);
            }

            return (
                <Space size="small">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontSize: '12px' }}>
                        {displayValue.length > 60 ? displayValue.substring(0, 60) + '...' : displayValue}
                    </Text>
                </Space>
            );
        }

        return null;
    };

    return (
        <div className="js-expression-editor">
            <Space direction="vertical" style={{ width: '100%' }}>
                {label && <Text strong>{label}</Text>}
                <div>
                    {/* Editor container with border */}
                    <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        overflow: 'visible',
                        position: 'relative',
                        ...ultraCompactStyles.editorContainer
                    }}>
                        <Editor
                            height={height}
                            language="javascript"
                            theme="vs-light"
                            value={expression}
                            options={getEditorOptions()}
                            onChange={handleExpressionChange}
                            onMount={handleEditorDidMount}
                        />
                        {ultraCompact && renderUltraCompactIndicators()}
                    </div>

                    {/* Mode toggle and evaluation result below the editor */}
                    {!ultraCompact && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 4
                        }}>
                            {/* Left side - evaluation result */}
                            <div>
                                {!showSampleData && renderCompactEvaluationResult()}
                            </div>

                            {/* Right side - toggles and controls */}
                            <Space size="small">
                                {/* Sample data toggle button */}
                                <Tooltip title={showSampleData ? "Hide sample data" : "Show sample data"}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={showSampleData ?
                                            <EyeInvisibleOutlined style={{ color: '#1890ff' }} /> :
                                            <EyeOutlined style={{ color: '#1890ff' }} />
                                        }
                                        onClick={toggleSampleData}
                                    />
                                </Tooltip>

                                {/* Mode toggle */}
                                <Radio.Group
                                    value={mode}
                                    onChange={(e) => handleModeChange(e.target.value)}
                                    optionType="button"
                                    buttonStyle="solid"
                                    size="small"
                                >
                                    <Radio.Button value="expression">
                                        <FormOutlined /> Expression
                                    </Radio.Button>
                                    <Radio.Button value="function">
                                        <CodeOutlined /> Function
                                    </Radio.Button>
                                </Radio.Group>
                            </Space>
                        </div>
                    )}
                </div>

                {/* Show full evaluation result when not in ultra-compact mode */}
                {!ultraCompact && showSampleData && (evaluationResult !== undefined || evaluationError !== null || shouldShowUndefined) && renderEvaluationResult()}

                {/* Conditionally show sample data viewer */}
                {showSampleData && (
                    <SampleDataViewer
                        onInsertText={insertTextAtCursor}
                        onExecuteTest={() => {
                            if (sampleDataRef.current) {
                                try {
                                    evaluateExpression(expression);
                                } catch (e) {
                                    console.error("Error executing test:", e);
                                    setEvaluationError(e.message);
                                }
                            } else {
                                setEvaluationError("No sample data available");
                            }
                        }}
                    />
                )}
            </Space>
        </div>
    );
};

export default JavaScriptExpressionEditor;
